import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { Calendar, Popover } from 'antd'
import { Icon } from '@iconify/react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import styles from './DateRangePicker.module.css'

dayjs.extend(customParseFormat)

const DATE_PARSE_FORMATS = [
    'DD/MM/YY',
    'D/M/YY',
    'DD-MM-YY',
    'D-M-YY',
    'DD/MM/YYYY',
    'D/M/YYYY',
    'DD-MM-YYYY',
    'D-M-YYYY',
]

const DEFAULT_INPUT_DATE_FORMAT = 'DD/MM/YY'
const POPOVER_WIDTH_ESTIMATE = 280
const POPOVER_HEIGHT_ESTIMATE = 420
const VIEWPORT_EDGE_GAP = 8
const PLACEMENT_GAP = 4

/**
 * Pick the placement where the popover fits best in the viewport.
 * Right-side drawers: prefer opening to the left (over the board) so the
 * calendar does not cover Description / Comments inside the drawer.
 */
const resolveBestPlacement = (triggerEl) => {
    if (!triggerEl || typeof window === 'undefined') return 'bottomLeft'

    const rect = triggerEl.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const w = Math.min(POPOVER_WIDTH_ESTIMATE, vw - VIEWPORT_EDGE_GAP * 2)
    const h = Math.min(POPOVER_HEIGHT_ESTIMATE, vh - VIEWPORT_EDGE_GAP * 2)
    const preferRightAligned = rect.left > vw * 0.45

    // Prefer opening beside the drawer (left) so Description/Comments stay visible.
    const candidates = preferRightAligned
        ? [
              'leftTop',
              'left',
              'leftBottom',
              'topRight',
              'topLeft',
              'top',
              'bottomRight',
              'bottomLeft',
              'bottom',
              'rightTop',
              'rightBottom',
              'right',
          ]
        : [
              'bottomLeft',
              'bottomRight',
              'bottom',
              'topLeft',
              'topRight',
              'top',
              'rightTop',
              'rightBottom',
              'right',
              'leftTop',
              'leftBottom',
              'left',
          ]

    const getOrigin = (placement) => {
        switch (placement) {
            case 'bottomLeft':
                return { left: rect.left, top: rect.bottom + PLACEMENT_GAP }
            case 'bottomRight':
                return {
                    left: rect.right - w,
                    top: rect.bottom + PLACEMENT_GAP,
                }
            case 'bottom':
                return {
                    left: rect.left + rect.width / 2 - w / 2,
                    top: rect.bottom + PLACEMENT_GAP,
                }
            case 'topLeft':
                return { left: rect.left, top: rect.top - h - PLACEMENT_GAP }
            case 'topRight':
                return {
                    left: rect.right - w,
                    top: rect.top - h - PLACEMENT_GAP,
                }
            case 'top':
                return {
                    left: rect.left + rect.width / 2 - w / 2,
                    top: rect.top - h - PLACEMENT_GAP,
                }
            case 'rightTop':
                return { left: rect.right + PLACEMENT_GAP, top: rect.top }
            case 'rightBottom':
                return {
                    left: rect.right + PLACEMENT_GAP,
                    top: rect.bottom - h,
                }
            case 'right':
                return {
                    left: rect.right + PLACEMENT_GAP,
                    top: rect.top + rect.height / 2 - h / 2,
                }
            case 'leftTop':
                return { left: rect.left - w - PLACEMENT_GAP, top: rect.top }
            case 'leftBottom':
                return {
                    left: rect.left - w - PLACEMENT_GAP,
                    top: rect.bottom - h,
                }
            case 'left':
                return {
                    left: rect.left - w - PLACEMENT_GAP,
                    top: rect.top + rect.height / 2 - h / 2,
                }
            default:
                return { left: rect.left, top: rect.bottom + PLACEMENT_GAP }
        }
    }

    const getCommentsFooterTop = () => {
        const root =
            triggerEl.closest('[class*="drawerContainer"]') ||
            triggerEl.closest('[class*="drawer"]') ||
            document.body
        const footer =
            root.querySelector('[class*="commentsFooter"]') ||
            root.querySelector('[class*="commentInputFooter"]')
        return footer ? footer.getBoundingClientRect().top : null
    }

    const commentsTop = getCommentsFooterTop()

    const scorePlacement = (placement) => {
        const { left, top } = getOrigin(placement)
        const overflowX =
            Math.max(0, VIEWPORT_EDGE_GAP - left) +
            Math.max(0, left + w - (vw - VIEWPORT_EDGE_GAP))
        const overflowY =
            Math.max(0, VIEWPORT_EDGE_GAP - top) +
            Math.max(0, top + h - (vh - VIEWPORT_EDGE_GAP))

        // Heavy penalty if popover would cover the sticky comments footer.
        let commentsPenalty = 0
        if (commentsTop != null) {
            const popoverBottom = top + h
            if (popoverBottom > commentsTop + 8) {
                commentsPenalty = (popoverBottom - commentsTop) * 2
            }
        }

        // Soft preference: open beside drawer when on the right side.
        const sideBonus =
            preferRightAligned && String(placement).startsWith('left') ? -40 : 0

        return overflowX + overflowY + commentsPenalty + sideBonus
    }

    let best = preferRightAligned ? 'leftTop' : 'bottomLeft'
    let bestScore = Number.POSITIVE_INFINITY
    for (const placement of candidates) {
        const score = scorePlacement(placement)
        if (score < bestScore) {
            bestScore = score
            best = placement
            if (score <= 0) break
        }
    }
    return best
}

const parseManualDate = (text) => {
    const trimmed = String(text ?? '').trim()
    if (!trimmed) return null

    for (const fmt of DATE_PARSE_FORMATS) {
        const parsed = dayjs(trimmed, fmt, true)
        if (parsed.isValid()) return parsed.startOf('day')
    }

    const loose = dayjs(trimmed)
    return loose.isValid() ? loose.startOf('day') : null
}

const normalizeRange = (start, end, editedField) => {
    let nextStart = start
    let nextEnd = end

    if (nextStart && nextEnd && nextStart.isAfter(nextEnd, 'day')) {
        if (editedField === 'start') {
            nextEnd = nextStart
        } else {
            nextStart = nextEnd
        }
    }

    return {
        start: nextStart?.isValid() ? nextStart.valueOf() : null,
        end: nextEnd?.isValid() ? nextEnd.valueOf() : null,
    }
}

const DateRangePicker = ({
    value,
    onChange,
    placeholder = 'No due date',
    parentComponent,
    getPopupContainer,
    dateFormat,
    showClear = true,
    highlightOverdue = false,
    disabled = false,
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
}) => {
    const isDisabled = disabled || typeof onChange !== 'function'
    const isDetailDrawerPicker =
        parentComponent === 'taskDetailView' ||
        parentComponent === 'storyMapReleaseDetail'

    const inputDateFormat = dateFormat || DEFAULT_INPUT_DATE_FORMAT

    const formatInputDate = useCallback(
        (d) => (d?.isValid() ? d.format(inputDateFormat) : ''),
        [inputDateFormat]
    )
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
    const isControlled = controlledOpen !== undefined
    const open = isControlled ? Boolean(controlledOpen) : uncontrolledOpen
    const setOpen = useCallback(
        (nextOpen) => {
            if (!isControlled) setUncontrolledOpen(nextOpen)
            controlledOnOpenChange?.(nextOpen)
        },
        [isControlled, controlledOnOpenChange]
    )
    const [placement, setPlacement] = useState(
        isDetailDrawerPicker ? 'leftTop' : 'bottomLeft'
    )
    const [activeTab, setActiveTab] = useState('start') // 'start' | 'due'
    const [startInputText, setStartInputText] = useState('')
    const [dueInputText, setDueInputText] = useState('')
    // Tracks the month/year the user has navigated to in the calendar panel.
    // This is purely a VIEW state — it does not affect the committed start/end value.
    const [calendarViewDate, setCalendarViewDate] = useState(null)
    const panelRef = useRef(null)
    const triggerRef = useRef(null)
    const calendarWrapperRef = useRef(null)
    const startInputRef = useRef(null)
    const dueInputRef = useRef(null)
    const activeTabRef = useRef(activeTab)
    const inputPointerIntentRef = useRef(null)
    const dateSelectedRef = useRef(false)
    const suppressBlurCommitRef = useRef(false)
    const calendarInteractionRef = useRef(false)

    activeTabRef.current = activeTab

    // Keep the ref synchronous so a calendar event in the same interaction
    // always commits to the field the user explicitly selected.
    const activateTab = useCallback((field) => {
        activeTabRef.current = field
        setActiveTab(field)
        setCalendarViewDate(null)
    }, [])

    const handleOpenChange = useCallback(
        (nextOpen) => {
            if (isDisabled) {
                setOpen(false)
                return
            }
            if (nextOpen) {
                setPlacement(resolveBestPlacement(triggerRef.current))
            }
            setOpen(nextOpen)
        },
        [isDisabled, setOpen]
    )

    const armCalendarInteraction = useCallback(() => {
        calendarInteractionRef.current = true
        window.setTimeout(() => {
            calendarInteractionRef.current = false
        }, 500)
    }, [])

    const scheduleBlurCommitResume = useCallback(() => {
        suppressBlurCommitRef.current = true
        window.setTimeout(() => {
            suppressBlurCommitRef.current = false
        }, 500)
    }, [])

    const startDate = useMemo(() => {
        if (!value?.start) return null
        const d = dayjs(value.start)
        return d.isValid() ? d.startOf('day') : null
    }, [value?.start])

    const endDate = useMemo(() => {
        if (!value?.end) return null
        const d = dayjs(value.end)
        return d.isValid() ? d.startOf('day') : null
    }, [value?.end])

    const startDateRef = useRef(startDate)
    const endDateRef = useRef(endDate)
    startDateRef.current = startDate
    endDateRef.current = endDate
    // Ant Design Calendar modes are only 'month' (day grid) | 'year' (month grid).
    const panelModeRef = useRef('month')

    const syncInputsFromValue = useCallback(() => {
        setStartInputText(formatInputDate(startDate))
        setDueInputText(formatInputDate(endDate))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate])

    // Keep displayed input values synchronized without changing which field the
    // user is editing. This runs after a start/end commit while the popover is open.
    useEffect(() => {
        if (open) syncInputsFromValue()
    }, [open, syncInputsFromValue])

    // Opening behavior only: start on the Start field. Do not tie this effect to
    // start/end values, otherwise selecting a Due date shifts the active tab back.
    useEffect(() => {
        if (!open) return
        activateTab('start')
        panelModeRef.current = 'month'
        setPlacement(resolveBestPlacement(triggerRef.current))
    }, [open, activateTab])

    const calendarPanelValue = useMemo(() => {
        // If the user has navigated the calendar, show that navigated month/year.
        // calendarViewDate is never committed — it is only a display position.
        if (calendarViewDate) return calendarViewDate

        // Otherwise show the committed date for the active tab.
        if (activeTab === 'start') {
            if (startDate) return startDate
            if (endDate) return endDate
            return dayjs()
        }
        if (endDate) return endDate
        if (startDate) return startDate
        return dayjs()
    }, [calendarViewDate, activeTab, startDate, endDate])

    const commitRange = useCallback(
        (nextStart, nextEnd, editedField, options = {}) => {
            const { switchToDue = false } = options
            const normalized = normalizeRange(nextStart, nextEnd, editedField)
            onChange?.(normalized)

            const committedStart = normalized.start
                ? dayjs(normalized.start).startOf('day')
                : null
            const committedEnd = normalized.end
                ? dayjs(normalized.end).startOf('day')
                : null

            // Parent value updates can be asynchronous. Keep event handlers on
            // the latest committed range immediately.
            startDateRef.current = committedStart
            endDateRef.current = committedEnd
            setStartInputText(formatInputDate(committedStart))
            setDueInputText(formatInputDate(committedEnd))

            if (switchToDue) {
                activateTab('due')
                scheduleBlurCommitResume()
            }
        },
        [onChange, formatInputDate, activateTab, scheduleBlurCommitResume]
    )

    // After month/year UI closes, Ant Select often focus-restores onto start/due.
    // That flips activeTab via onFocus and makes disabledDate look wrong (e.g.
    // due tab suddenly treats end as a max and disables days after the 17th).
    const keepFocusOnCalendar = useCallback(() => {
        scheduleBlurCommitResume()
        armCalendarInteraction()
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                const active = document.activeElement
                if (
                    active === startInputRef.current ||
                    active === dueInputRef.current
                ) {
                    active.blur()
                }
                calendarWrapperRef.current?.focus({ preventScroll: true })
            })
        })
    }, [scheduleBlurCommitResume, armCalendarInteraction])

    // When month/year changes (header select or year-panel month click), keep the
    // same day number when possible. Example: 17/08/2026 + July → 17/07/2026.
    const applyMonthYearToActiveDate = useCallback(
        (nextView) => {
            if (
                !nextView ||
                typeof nextView.isValid !== 'function' ||
                !nextView.isValid()
            ) {
                return
            }

            const tab = activeTabRef.current
            const baseDate =
                tab === 'start' ? startDateRef.current : endDateRef.current
            // No committed value yet → treat "today" as the day/month reference
            // (matches the default start/end display).
            const reference = baseDate || dayjs().startOf('day')

            if (
                reference.month() === nextView.month() &&
                reference.year() === nextView.year()
            ) {
                return
            }

            const preferredDay = reference.date()
            const clampedDay = Math.min(preferredDay, nextView.daysInMonth())
            let nextDate = nextView.date(clampedDay).startOf('day')

            if (tab === 'start') {
                // normalizeRange may bump end forward if start moves past it.
                commitRange(nextDate, endDateRef.current, 'start')
            } else {
                const start = startDateRef.current
                // Don't pull start backward when month-nav lands before start —
                // floor end at start so later days in the month stay selectable.
                if (start && nextDate.isBefore(start, 'day')) {
                    nextDate = start
                }
                commitRange(start, nextDate, 'due')
            }
        },
        [commitRange]
    )

    const handleDateChange = (date) => {
        if (!date) return
        if (typeof date.isValid === 'function' && !date.isValid()) return
        const d = date.startOf('day')

        // ALWAYS update the calendar view state so the panel reflects the
        // navigated month/year. This is what was missing in the previous fix —
        // without this, the controlled `value` prop never changed and the Calendar
        // snapped back to the committed date's month on every render.
        setCalendarViewDate(d)

        // Header Month/Year selects fire onChange without a cell click — those
        // are handled in onPanelChange via applyMonthYearToActiveDate.
        if (!dateSelectedRef.current) return
        dateSelectedRef.current = false

        // Year panel = month grid: clicking a month should update month only,
        // not treat it like picking a day (and not jump to the due tab).
        if (panelModeRef.current === 'year') {
            applyMonthYearToActiveDate(d)
            keepFocusOnCalendar()
            return
        }

        const tab = activeTabRef.current
        if (tab === 'start') {
            commitRange(d, endDateRef.current, 'start', {
                switchToDue: true,
            })
        } else {
            commitRange(startDateRef.current, d, 'due')
        }
    }

    const handleCalendarKeyDown = (e) => {
        if (e.key === 'Enter') {
            dateSelectedRef.current = true
        }
    }

    // Ant Design Calendar: mode is 'month' (days) | 'year' (months).
    // MonthSelect/YearSelect keep mode and fire onPanelChange with the new date.
    const handlePanelChange = (date, mode) => {
        panelModeRef.current = mode === 'year' ? 'year' : 'month'

        if (!date || typeof date.isValid !== 'function' || !date.isValid()) {
            keepFocusOnCalendar()
            return
        }

        const nextView = date.startOf('day')
        setCalendarViewDate(nextView)
        applyMonthYearToActiveDate(nextView)
        keepFocusOnCalendar()
    }

    const shouldSkipBlurCommit = () =>
        suppressBlurCommitRef.current || calendarInteractionRef.current

    const commitInputBlur = (field) => {
        if (shouldSkipBlurCommit()) return

        const text = field === 'start' ? startInputText : dueInputText
        const trimmed = text.trim()

        if (!trimmed) {
            if (field === 'start') {
                if (startDate) {
                    setStartInputText(formatInputDate(startDate))
                    return
                }
                commitRange(null, endDate, 'start')
            } else {
                // Due field is often empty while picking an end date from the calendar.
                // Sync from props instead of emitting end: null on blur.
                setDueInputText(formatInputDate(endDate))
            }
            return
        }

        const parsed = parseManualDate(trimmed)
        if (!parsed) {
            if (field === 'start') {
                setStartInputText(formatInputDate(startDate))
            } else {
                setDueInputText(formatInputDate(endDate))
            }
            return
        }

        const sameAsCurrent =
            field === 'start'
                ? startDate?.isSame(parsed, 'day')
                : endDate?.isSame(parsed, 'day')

        if (field === 'start') {
            if (!sameAsCurrent) {
                commitRange(parsed, endDate, 'start')
            } else {
                setStartInputText(formatInputDate(startDate))
            }
        } else if (!sameAsCurrent) {
            commitRange(startDate, parsed, 'due')
        } else {
            setDueInputText(formatInputDate(endDate))
        }
    }

    /** Defer blur so calendar clicks / tab switch don't commit with wrong active tab */
    const handleInputBlur = (field) => {
        window.setTimeout(() => {
            if (shouldSkipBlurCommit()) return
            if (panelRef.current?.contains(document.activeElement)) {
                return
            }
            commitInputBlur(field)
        }, 0)
    }

    const handleCalendarMouseDown = (e) => {
        if (e.target.closest('.ant-picker-cell')) {
            e.preventDefault()
            dateSelectedRef.current = true
            armCalendarInteraction()
            return
        }
        // Month/year header — keep activeTab stable; don't let focus land on inputs.
        if (
            e.target.closest('.ant-picker-calendar-header') ||
            e.target.closest('.ant-select') ||
            e.target.closest('.ant-radio-group')
        ) {
            armCalendarInteraction()
            scheduleBlurCommitResume()
        }
    }

    const disabledDate = (current) => {
        if (!current) return false
        // Use refs so a transient focus flip can't apply the wrong bound mid-click.
        const tab = activeTabRef.current
        if (tab === 'start' && endDateRef.current) {
            return current.isAfter(endDateRef.current, 'day')
        }
        if (tab === 'due' && startDateRef.current) {
            return current.isBefore(startDateRef.current, 'day')
        }
        return false
    }

    const handleClear = (e) => {
        e.stopPropagation()
        onChange?.({ start: null, end: null })
        setStartInputText('')
        setDueInputText('')
        setOpen(false)
    }

    const formatDateRange = () => {
        if (dateFormat) {
            if (startDate && endDate) {
                return `${startDate.format(dateFormat)} – ${endDate.format(dateFormat)}`
            }
            if (endDate) return endDate.format(dateFormat)
            if (startDate) return startDate.format(dateFormat)
            return null
        }

        const currentYear = dayjs().year()

        const formatDate = (d) =>
            d.year() === currentYear
                ? d.format('D MMM')
                : d.format('D MMM, YYYY')

        if (startDate && endDate) {
            if (startDate.isSame(endDate, 'month')) {
                // Same month + year: "29 – 30 Sep" or "29 – 30 Sep, 2024"
                const monthSuffix =
                    startDate.year() === currentYear
                        ? startDate.format('MMM')
                        : startDate.format('MMM, YYYY')
                return `${startDate.format('D')} – ${endDate.format('D')} ${monthSuffix}`
            }
            // Different month or year: "29 Aug – 3 Sep" or "29 Aug, 2024 – 3 Sep, 2024"
            return `${formatDate(startDate)} – ${formatDate(endDate)}`
        }
        if (endDate) return formatDate(endDate)
        if (startDate) return formatDate(startDate)
        return null
    }

    const dateRangeText = formatDateRange()
    const overdueDate = endDate || startDate
    const isOverdue =
        highlightOverdue &&
        overdueDate?.isValid() &&
        overdueDate.isBefore(dayjs(), 'day')

    const content = (
        <div ref={panelRef} className={styles.datePickerContainer}>
            <div className={styles.tabs} role="tablist" aria-label="Date range">
                <div
                    className={`${styles.tabField} ${
                        activeTab === 'start' ? styles.activeTab : ''
                    }`}
                >
                    <input
                        ref={startInputRef}
                        type="text"
                        role="tab"
                        aria-selected={activeTab === 'start'}
                        className={styles.tabInput}
                        placeholder="Start date"
                        value={startInputText}
                        onPointerDown={() => {
                            inputPointerIntentRef.current = 'start'
                            activateTab('start')
                        }}
                        onFocus={() => {
                            // Month/year select can steal focus onto this input —
                            // don't switch tabs or reset the calendar view.
                            const isIntentionalPointerFocus =
                                inputPointerIntentRef.current === 'start'
                            inputPointerIntentRef.current = null
                            if (
                                !isIntentionalPointerFocus &&
                                (calendarInteractionRef.current ||
                                    suppressBlurCommitRef.current)
                            ) {
                                keepFocusOnCalendar()
                                return
                            }
                            activateTab('start')
                        }}
                        onChange={(e) => setStartInputText(e.target.value)}
                        onBlur={() => handleInputBlur('start')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur()
                        }}
                    />
                </div>
                <div
                    className={`${styles.tabField} ${
                        activeTab === 'due' ? styles.activeTab : ''
                    }`}
                >
                    <input
                        ref={dueInputRef}
                        type="text"
                        role="tab"
                        aria-selected={activeTab === 'due'}
                        className={styles.tabInput}
                        placeholder="Due date"
                        value={dueInputText}
                        onPointerDown={() => {
                            inputPointerIntentRef.current = 'due'
                            activateTab('due')
                        }}
                        onFocus={() => {
                            const isIntentionalPointerFocus =
                                inputPointerIntentRef.current === 'due'
                            inputPointerIntentRef.current = null
                            if (
                                !isIntentionalPointerFocus &&
                                (calendarInteractionRef.current ||
                                    suppressBlurCommitRef.current)
                            ) {
                                keepFocusOnCalendar()
                                return
                            }
                            activateTab('due')
                        }}
                        onChange={(e) => setDueInputText(e.target.value)}
                        onBlur={() => handleInputBlur('due')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur()
                        }}
                    />
                </div>
            </div>
            <div
                ref={calendarWrapperRef}
                className={styles.calendarWrapper}
                tabIndex={-1}
                onMouseDownCapture={handleCalendarMouseDown}
                onKeyDown={handleCalendarKeyDown}
            >
                <Calendar
                    value={calendarPanelValue}
                    onChange={handleDateChange}
                    onPanelChange={handlePanelChange}
                    disabledDate={disabledDate}
                    className={styles.calendarPicker}
                    fullscreen={false}
                />
            </div>
        </div>
    )

    return (
        <Popover
            content={content}
            trigger={isDisabled ? [] : 'click'}
            open={isDisabled ? false : open}
            onOpenChange={handleOpenChange}
            placement={placement}
            autoAdjustOverflow
            align={{
                overflow: {
                    adjustX: true,
                    adjustY: true,
                },
            }}
            overlayClassName={styles.datePickerPopover}
            overlayStyle={{ maxWidth: 'calc(100vw - 24px)' }}
            getPopupContainer={getPopupContainer || (() => document.body)}
            destroyOnHidden
        >
            <div
                ref={triggerRef}
                className={styles.datePickerTriggerWrap}
                style={isDisabled ? { cursor: 'default' } : undefined}
            >
                {dateRangeText ? (
                    <div
                        className={`${styles.dateRangeChip} ${
                            isDetailDrawerPicker
                                ? styles.dateRangeChipHover
                                : ''
                        }`}
                        style={{
                            padding:
                                parentComponent === 'taskListView'
                                    ? '6px 0px'
                                    : '6px 12px',
                        }}
                    >
                        {isDetailDrawerPicker && (
                            <Icon
                                icon="akar-icons:calendar"
                                width={16}
                                height={16}
                                className={styles.calendarIcon}
                            />
                        )}
                        <span
                            className={`${styles.dateRangeText} ${
                                isOverdue
                                    ? styles.dateRangeTextOverdue
                                    : ''
                            }`}
                            style={{
                                fontSize: isDetailDrawerPicker ? 14 : 12,
                            }}
                        >
                            {dateRangeText}
                        </span>
                        {isDetailDrawerPicker && showClear && (
                            <Icon
                                icon="mdi:close"
                                width={14}
                                height={14}
                                className={styles.closeIcon}
                                onClick={handleClear}
                            />
                        )}
                    </div>
                ) : (
                    <div className={styles.datePickerTrigger}>
                        <div
                            className={styles.iconWrapper}
                            style={{
                                width: isDetailDrawerPicker ? '28px' : '24px',
                                height: isDetailDrawerPicker ? '28px' : '24px',
                            }}
                        >
                            <Icon
                                icon="akar-icons:calendar"
                                width={isDetailDrawerPicker ? 16 : 14}
                                height={isDetailDrawerPicker ? 16 : 14}
                                style={{ color: '#94a3b8' }}
                            />
                        </div>
                        <span className={styles.placeholderText}>
                            {placeholder}
                        </span>
                    </div>
                )}
            </div>
        </Popover>
    )
}

export default DateRangePicker
